import { KeyboardEvent, ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

export type InlinePickerOption = {
  id: string;
  label: string;
  description?: string;
};

type InlineOptionPickerProps = {
  valueId?: string | null;
  valueLabel?: string | null;
  options: InlinePickerOption[];
  emptyLabel?: string;
  disabled?: boolean;
  saving?: boolean;
  allowClear?: boolean;
  clearLabel?: string;
  searchable?: boolean;
  triggerClassName?: string;
  renderTrigger?: (label: string, open: boolean) => ReactNode;
  onCommit: (option: InlinePickerOption | null) => void | Promise<void>;
};

type MenuPosition = {
  top: number;
  left: number;
  width: number;
  maxHeight: number;
};

export function InlineOptionPicker({
  valueId,
  valueLabel,
  options,
  emptyLabel = 'Selecionar',
  disabled = false,
  saving = false,
  allowClear = false,
  clearLabel = 'Limpar',
  searchable = true,
  triggerClassName = '',
  renderTrigger,
  onCommit,
}: InlineOptionPickerProps) {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [menuPosition, setMenuPosition] = useState<MenuPosition>({
    top: 0,
    left: 0,
    width: 240,
    maxHeight: 280,
  });

  const selectedLabel = valueLabel?.trim() || emptyLabel;

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(term)
        || Boolean(option.description?.toLowerCase().includes(term)),
    );
  }, [options, query]);

  const menuItems = useMemo(() => {
    if (!allowClear) return filtered;
    return [{ id: '', label: clearLabel }, ...filtered];
  }, [allowClear, clearLabel, filtered]);

  function updateMenuPosition(): void {
    const rect = inputRef.current?.getBoundingClientRect() ?? rootRef.current?.getBoundingClientRect();
    if (!rect) return;
    const width = Math.max(rect.width, 220);
    const maxLeft = window.innerWidth - width - 8;
    const gap = 4;
    const padding = 8;
    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUpward = spaceBelow < 160 && spaceAbove > spaceBelow;
    const available = Math.max(120, (openUpward ? spaceAbove : spaceBelow) - gap);
    const maxHeight = Math.min(280, available);
    setMenuPosition({
      top: openUpward ? rect.top - gap - maxHeight : rect.bottom + gap,
      left: Math.max(8, Math.min(rect.left, maxLeft)),
      width,
      maxHeight,
    });
  }

  useEffect(() => {
    if (!open) return;
    updateMenuPosition();
    const onScroll = () => updateMenuPosition();
    window.addEventListener('resize', onScroll);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('resize', onScroll);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handle = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      const menu = document.getElementById(listId);
      if (menu?.contains(target)) return;
      setOpen(false);
      setQuery('');
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, listId]);

  useEffect(() => {
    if (open && searchable) {
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open, searchable]);

  async function commit(option: InlinePickerOption | null): Promise<void> {
    setOpen(false);
    setQuery('');
    await onCommit(option);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      setOpen(false);
      setQuery('');
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.min(index + 1, Math.max(menuItems.length - 1, 0)));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex((index) => Math.max(index - 1, 0));
      return;
    }

    if (event.key === 'Enter') {
      event.preventDefault();
      const option = menuItems[highlightedIndex];
      if (!option) return;
      void commit(option.id ? option : null);
    }
  }

  return (
    <div ref={rootRef} className="relative inline-flex min-w-0 max-w-full">
      {open && searchable ? (
        <input
          ref={inputRef}
          className="erp-input w-full min-w-[10rem] text-sm"
          value={query}
          placeholder={selectedLabel}
          disabled={disabled || saving}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlightedIndex(0);
          }}
          onKeyDown={onKeyDown}
        />
      ) : (
        <button
          type="button"
          disabled={disabled || saving}
          className={
            triggerClassName
            || 'inline-flex max-w-full items-center rounded-md px-1.5 py-0.5 text-left text-sm font-medium text-secondary-800 ring-1 ring-inset ring-secondary-200 hover:bg-secondary-50 disabled:opacity-60'
          }
          onClick={() => {
            if (disabled || saving) return;
            setOpen(true);
            setQuery('');
            setHighlightedIndex(0);
          }}
        >
          {renderTrigger ? renderTrigger(selectedLabel, open) : (
            <span className="truncate">{saving ? 'Salvando...' : selectedLabel}</span>
          )}
        </button>
      )}

      {open
        ? createPortal(
            <ul
              id={listId}
              role="listbox"
              className="z-[80] overflow-auto rounded-lg border border-secondary-200 bg-white py-1 shadow-lg"
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                width: menuPosition.width,
                maxHeight: menuPosition.maxHeight,
              }}
            >
              {menuItems.length === 0 ? (
                <li className="px-3 py-2 text-sm text-secondary-500">Nenhuma opção</li>
              ) : (
                menuItems.map((option, index) => {
                  const active = index === highlightedIndex;
                  const selected = option.id !== '' && option.id === valueId;
                  return (
                    <li key={`${option.id || 'clear'}-${option.label}`}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`flex w-full flex-col px-3 py-2 text-left text-sm ${
                          active ? 'bg-primary-50 text-primary-900' : 'text-secondary-800 hover:bg-secondary-50'
                        }`}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onClick={() => void commit(option.id ? option : null)}
                      >
                        <span className="font-medium">{option.label}</span>
                        {option.description ? (
                          <span className="text-xs text-secondary-500">{option.description}</span>
                        ) : null}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>,
            document.body,
          )
        : null}
    </div>
  );
}
